import { UseQueryStateResult } from '@reduxjs/toolkit/dist/query/react/buildHooks.d'
import { createApi } from '@reduxjs/toolkit/query/react'

import baseQueryInterceptor from '@api/base-query-interceptor'
import { ApiResponse } from '@api/types/api-response.type'

import { roomQueryParams } from '@utilities/helper'

import {
  MatrixDataInterface,
  PackageOptionsInterface,
  PrecinctListItemInterface,
} from './types/house-and-land-type'

type PackageByIdRequestParameters = {
  projectName: string
  packageIds?: string
  lotId?: string
}

type MatrixDataRequestParameters = {
  projectName: string
  precinctIdOrLabel: string
  lots?: boolean
}

type MatrixDataRequestByPrecinctIdsParameters = {
  projectName: string
  precinctIdsOrLabels: string[]
  lots?: boolean
}

type PrecinctListRequestParameters = {
  projectName: string
}

const API_VERSION = 'v3'

export const houseAndLandApi = createApi({
  reducerPath: 'houseAndLandApi',
  baseQuery: baseQueryInterceptor,
  endpoints: (builder) => ({
    getMatrixDataByPrecinct: builder.query<
      ApiResponse<MatrixDataInterface>,
      MatrixDataRequestParameters
    >({
      query: (params: MatrixDataRequestParameters) => ({
        url: `/${API_VERSION}/house-and-land/${params.projectName}/packages/summary`,
        method: 'get',
        params: {
          ...roomQueryParams(),
          precinctIdOrLabel: params.precinctIdOrLabel,
          lots: params.lots || false,
        },
      }),
    }),

    getPackagesById: builder.query<
      ApiResponse<Array<PackageOptionsInterface>>,
      PackageByIdRequestParameters
    >({
      query: (params: PackageByIdRequestParameters) => ({
        url: `/${API_VERSION}/house-and-land/${params.projectName}/packages`,
        method: 'get',
        params: {
          ...roomQueryParams(),
          packageIds: params.packageIds,
          lotId: params.lotId,
        },
      }),
    }),

    getPrecinctList: builder.query<
      ApiResponse<Array<PrecinctListItemInterface>>,
      PrecinctListRequestParameters
    >({
      query: (params: PrecinctListRequestParameters) => ({
        url: `/${API_VERSION}/house-and-land/${params.projectName}/precincts/list`,
        method: 'get',
      }),
    }),
  }),
})

const extendedHouseAndLandApi = houseAndLandApi.injectEndpoints({
  endpoints: (builder) => ({
    getMatrixDataByPrecinctIds: builder.query<
      ApiResponse<MatrixDataInterface>[],
      MatrixDataRequestByPrecinctIdsParameters
    >({
      async queryFn(arg, api) {
        const { dispatch } = api

        try {
          const results = await Promise.all(
            arg.precinctIdsOrLabels.map((id) =>
              dispatch(
                houseAndLandApi.endpoints.getMatrixDataByPrecinct.initiate({
                  ...arg,
                  precinctIdOrLabel: id,
                })
              ).unwrap()
            )
          )
          return { data: results }
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: String(error),
            },
          }
        }
      },
    }),
  }),
})

export const selectMatrixDataFromResult = ({
  data,
  isLoading,
  isError,
  isFetching,
  status,
  error,
}: UseQueryStateResult<any, any>): {
  matrixData: MatrixDataInterface
  isLoaded: boolean
  isError: boolean
  isFetching: boolean
  errorStatus: number
  status: string
} => ({
  matrixData: data?.data || {},
  isLoaded: !isLoading,
  isError,
  isFetching,
  status,
  errorStatus: error?.status,
})

export const selectMatrixDataByPrecinctIdsFromResult = ({
  data,
  isLoading,
  isError,
  isFetching,
  status,
  error,
}: UseQueryStateResult<any, any>): {
  data: MatrixDataInterface[]
  isLoaded: boolean
  isError: boolean
  isFetching: boolean
  errorStatus: number
  status: string
} => ({
  data: (data || []).map((item: { data: MatrixDataInterface }) => item.data),
  isLoaded: !isLoading,
  isError,
  isFetching,
  status,
  errorStatus: error?.status,
})

export const selectPackagesFromResult = ({
  data,
  isLoading,
  isError,
  status,
  error,
  isFetching,
}: UseQueryStateResult<any, any>): {
  packages: Array<PackageOptionsInterface>
  isLoaded: boolean
  isError: boolean
  errorStatus: number
  status: string
  isFetching: boolean
} => ({
  packages: data?.data?.data || [],
  isLoaded: !isLoading,
  isError,
  status,
  errorStatus: error?.status,
  isFetching,
})

export const selectPrecinctListFromResult = ({
  data,
  isLoading,
  isError,
  status,
  error,
}: UseQueryStateResult<any, any>): {
  precinctList: Array<PrecinctListItemInterface>
  isLoaded: boolean
  isError: boolean
  errorStatus: number
  status: string
} => ({
  precinctList: data?.data || [],
  isLoaded: !isLoading,
  isError,
  status,
  errorStatus: error?.status,
})

export const {
  useGetMatrixDataByPrecinctQuery,
  useLazyGetMatrixDataByPrecinctQuery,
  useGetPackagesByIdQuery,
  useGetPrecinctListQuery,
  useLazyGetPackagesByIdQuery,
} = houseAndLandApi

export const { useGetMatrixDataByPrecinctIdsQuery } = extendedHouseAndLandApi
